<template>
  <div class="Share">

    <div class="Share-header">

      <transition name="Share-contentForm-">

        <h2 class="t-headingSm"
          v-if="content.title && !submitted"
          v-html="content.title">
        </h2>

      </transition>

    </div>

    <div class="Share-content">

      <transition-group name="Share-contentForm-">

        <div class="Share-contentForm"
          v-if="!submitted"
          key="shareContentForm">

          <div class="Share-feedback"
            @click="clear">

            <div class="Share-feedbackText"
              v-text="feedback">
            </div>

            <icon class="Share-feedbackIcon"
              v-if="value"
              name="x" />

          </div>

          <p class="Share-errorMessage"
            v-if="error"
            v-html="error">
          </p>

          <div class="Share-keyboard">

            <div class="Share-keyboardRow"
              v-for="row in keyboards[mode]">

              <div class="Share-keyboardKey"
                v-for="key in row"
                :data-value="key"
                :class="keyClass(key)"
                @click="selectKey(key)">

                <span v-text="formatValue(key)"></span>

                <icon class="Share-keyboardKeyIcon"
                  v-if="key === 'shift' || key === 'backspace'"
                  :name="key === 'shift' ? 'arrow-up-fancy' : 'arrow-left-fancy'" />

              </div>

            </div>

          </div>

          <div class="Share-utilities">

            <div class="Share-keyboardKey Share-keyboardKey--isWide  Share-submit Share-keyboardKey--isActive Share-keyboardKey--untoggleable"
              @click="submit">Send</div>

          </div>

        </div>

        <div class="Share-contentMessage" v-if="submitted" key="shareContentMessage">

          <p v-html="submittedMessage"></p>

          <div class="Share-contentMessageButtons">

            <button-component small
              v-for="(button, index) in submittedButtons"
              :key="index"
              @click.native="button.method">
              {{ button.label }}
            </button-component>

          </div>

        </div>

      </transition-group>

    </div>

    <p class="Share-footer t-small"
      v-if="content.footer"
      v-html="content.footer">
    </p>

  </div>
</template>

<script>
import ButtonComponent from 'components/Button'
import Icon from 'components/Icon'

import libPhoneNumber from 'google-libphonenumber'

const CMS_SHARE_ENDPOINT = 'https://petspace-dev.azurewebsites.net/api/connect/shareanimal'

export default {
  name: 'Share',

  components: {
    ButtonComponent,
    Icon
  },

  props: {
    content: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      submitted: false,
      mode: 'alpha', // `alpha`, `symbols`, or `numeric`
      placeholders: {
        alpha: 'email address',
        symbols: 'email address',
        numeric: 'phone number'
      },
      value: '',
      error: false,
      shiftIsActive: false,
      keyboards: {
        alpha: [
          ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
          ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
          ['shift', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace'],
          ['123', '@', 'space', '.', '.com']
        ],
        symbols: [
          ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
          ['!', '?', '#', '$', '%', '&', '*', '^'],
          ['/', '|', '{', '}', '=', '+', '_', '-', 'backspace'],
          ['ABC', '@', '`', '~', "'", '.', '.com']
        ],
        numeric: [
          ['1', '2', '3'],
          ['4', '5', '6'],
          ['7', '8', '9'],
          ['0']
        ]
      }
    }
  },

  watch: {
    value () {
      this.error = false
    },

    mode () {
      this.error = false
    }
  },

  computed: {
    animal () { return this.$store.state.animal },
    feedback () {
      if (this.value === '') {
        return this.placeholders[this.mode]
      }

      return this.value
    },

    submittedMessage () {
      return "Thank you! <br>You'll be hearing from us soon!"
    },

    submittedButtons () {
      return []
    }
  },

  methods: {
    /**
     * Return value in proper case depending on this.shiftIsActive
     * @param  {String} value
     * @return {String}
     */
    formatValue (value) {
      if (value === '.com') return value

      if (this.shiftIsActive) return value.toUpperCase()
      return value
    },

    /**
     * Return classes for a key
     * @param  {String} value [description]
     * @return {String}
     */
    keyClass (value) {
      return {
        'Share-keyboardKey--untoggleable': value === 'shift',
        'Share-keyboardKey--isActive': value === 'shift' && this.shiftIsActive
      }
    },

    /**
     * Handle key selection
     * @param  {String} value
     */
    selectKey (value) {
      const specialKeys = {
        shift: this.selectShiftKey,
        backspace: this.selectBackspaceKey,
        space: this.selectSpaceKey,
        123: this.toggleSymbols,
        ABC: this.toggleSymbols
      }

      if (specialKeys.hasOwnProperty(value)) {
        return specialKeys[value]()
      }

      this.value += this.formatValue(value)
    },

    /**
     * Handle shift key selection
     */
    selectShiftKey () {
      this.shiftIsActive = !this.shiftIsActive
    },

    /**
     * Handle backspace key selection
     */
    selectBackspaceKey () {
      this.value = this.value.substring(0, this.value.length - 1)
    },

    /**
     * Handle space key selection
     */
    selectSpaceKey () {
      this.value += '_'
    },

    /**
     * Handle 123 key selection
     */
    toggleSymbols () {
      if (this.mode === 'symbols') {
        this.mode = 'alpha'
        return
      }

      this.mode = 'symbols'
    },

    /**
     * Clear feedback element
     * @return {null}
     */
    clear () {
      this.value = ''
      this.error = false
    },

    /**
     * Submit form
     * TODO: validation here?
     */
    submit () {
      if (this.mode === 'alpha' || this.mode === 'symbols') {
        this.validateEmail()
      } else {
        this.validatePhone()
      }
    },

    /**
     * Validate email and submit results
     */
    validateEmail () {
      /* eslint-disable no-useless-escape */
      const pattern = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
      const test = pattern.test(this.value)

      if (test) {
        this.post()
        return
      }

      this.error = 'The email address you entered does not appear to be valid.'
    },

    /**
     * Validate phone number and submit results
     */
    validatePhone () {
      // const test = libPhoneNumber.isValidNumber(this.value)
      const phoneUtil = libPhoneNumber.PhoneNumberUtil.getInstance()
      let test = false

      try {
        const parsed = phoneUtil.parse(this.value, 'US')
        test = phoneUtil.isValidNumber(parsed)
      } catch (err) {
        // console.log(err)
      }

      if (test) {
        this.post()
        return
      }

      this.error = 'The phone number you entered does not appear to be valid.'
    },

    /**
     * Go to add to favorites
     */
    goToFavorites () {
      if (this.$route.name === 'favorites') {
        this.submitted = false
        return
      }

      this.$router.push({
        name: 'favorites'
      })
    },

    /**
     * Share again
     */
    goToShare () {
      if (this.$route.name === 'share') {
        this.value = ''
        this.submitted = false
        return
      }

      this.$router.push({
        name: 'share'
      })
    },

    /**
     * Post valid email or phone number to CMS endpoint
     */
    post () {
      const type = (this.mode === 'alpha' || this.mode === 'symbols')
        ? 'EmailAddress'
        : 'PhoneNumber'

      const req = new XMLHttpRequest()

      this.error = 'Processing...'

      req.addEventListener('error', err => {
        console.log(err)
      })

      req.addEventListener('load', () => {
        this.error = false
        this.submitted = true
        this.$analytics.event(`${this.$route.name.charAt(0).toUpperCase() + this.$route.name.slice(1)}/${type}`)
      })

      req.open('POST', CMS_SHARE_ENDPOINT)
      req.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
      req.send(`${type}=${this.value}&ShelterLuvInternalId=${this.animal.shelterLuvId}`)
    }
  }
}
</script>

<style>
.Share {
  height: 460px;
  text-align: center;
}

.Share-header {
  margin: 0 auto;
  max-width: 530px;
  height: 160px;
}

.Share-content {
  position: relative;
  margin-top: 2rem;
  height: 330px;
}

.Share-contentForm {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.Share-contentForm--enter-active,
.Share-contentForm--leave-active {
  transition: opacity 0.5s ease;
}

.Share-contentForm--enter,
.Share-contentForm--leave-to {
  opacity: 0;
}

.Share-contentMessage {
  padding-top: 50px;
}

.Share-contentMessageButtons {
  margin-top: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.Share-contentMessageButtons .Button + .Button {
  margin-left: 11px;
}

.Share-feedback {
  position: relative;
  display: inline-block;
  padding: 0 14px;
  width: 400px;
  height: 39px;
  border: 2px solid var(--color-3);
  background-color: var(--color-1);
  border-radius: 24px;
  font-size: 15px;
  line-height: 2.5;
  letter-spacing: 0.15em;
  color: var(--color-5);
  text-align: left;
  overflow: hidden;
}

.Share-feedbackIcon {
  position: absolute;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
}

.Share-feedbackIcon svg {
  width: 9px;
  height: 9px;
  fill: var(--color-3);
}

.Share-errorMessage {
  position: absolute;
  text-align: center;
  width: 100%;
  font-size: 13px;
  font-weight: 500px;
}

.Share-keyboard {
  margin-top: 22px;
}

.Share-keyboardRow {
  display: flex;
  justify-content: center;
}

.Share-keyboardKey {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 3.5px;
  width: 34px;
  height: 34px;
  border: 2px solid var(--color-3);
  border-radius: 24px;
  background: var(--color-1);
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
}

.Share-keyboardKey:not(.Share-keyboardKey--untoggleable):active {
  outline: none;
  animation: keyfocus 0.1s;
}

@keyframes keyfocus {
  0% {
    background-color: var(--color-3);
    color: #fff;
  }
  100% {
    background-color: inherit;
  }
}

.Share-keyboardKey--isActive {
  border-color: var(--color-3);
  background: var(--color-3);
  color: #fff;
}

.Share-keyboardKey[data-value="shift"],
.Share-keyboardKey[data-value="backspace"],
.Share-keyboardKey[data-value="123"],
.Share-keyboardKey[data-value="ABC"],
.Share-keyboardKey[data-value=".com"] {
  width: 55px;
}

.Share-keyboardKey[data-value="shift"] span,
.Share-keyboardKey[data-value="backspace"] span,
.Share-keyboardKey[data-value="space"] span {
  display: none;
}

.Share-keyboardKeyIcon {
  fill: var(--color-3);
}

.Share-keyboardKey[data-value="shift"] .Share-keyboardKeyIcon {
  width: 10px;
  height: 12px;
}

.Share-keyboardKey[data-value="backspace"] .Share-keyboardKeyIcon {
  width: 12px;
  height: 10px;
}

.Share-keyboardKey--isActive .Share-keyboardKeyIcon svg {
  fill: var(--color-1);
}

.Share-keyboardKey[data-value="space"] {
  width: 195px;
}

.Share-utilities {
  margin-top: 21px;
}

.Share-toggle {
  width: 119px;
}

.Share-togglePhone,
.Share-toggleEmail {
  margin: 0;
  width: 55px;
  height: 24px;
  border-color: transparent;
}

.Share-submit {
  width: 264px;
}
</style>



// WEBPACK FOOTER //
// Share.vue?f8c2d954